import { ReactAmChart } from '../../ReactAmChart';

import { arrayOf, shape, string, number, bool } from 'prop-types';
import { isEqual, cloneDeep } from 'lodash';

export default class AdsPerformanceChart extends ReactAmChart {
  static propTypes = {
    data: arrayOf(
      shape({
        category: string,
        y: number,
        x: number
      })
    ),
    axisFontSize: number,
    titleFontSize: number,
    tooltipFontSize: number,
    yAxisTitle: string,
    dashStyle: string,
    fontColor: string,
    lineColor: string,
    fontFamily: string,
    rangeFillColor: string,
    tooltipFontColor: string,
    showSerieLabels: bool
  }

  static defaultProps = {
    data: [],
    axisFontSize: 12,
    titleFontSize: 14,
    tooltipFontSize: 12,
    dashStyle: '3, 3',
    yAxisTitle: 'Visits',
    lineColor: '#2196F3',
    fontColor: 'rgba(0, 0, 0, 0.54)',
    rangeFillColor: 'rgba(143,201,238,0.2)',
    tooltipFontColor: 'rgba(0, 0, 0, 0.87)',
    showSerieLabels: false,
    fontFamily: 'Roboto, "Helvetica Neue", sans-serif'
  }

  valueAxisY;
  prevChartData;
  drawFunction(chartId, prevChart, am4core, am4charts) {
    const { 
      data,
      yAxisTitle,
      dashStyle, 
      fontColor,
      fontFamily,
      lineColor, 
      rangeFillColor,
      tooltipFontColor,
      axisFontSize,
      titleFontSize,
      tooltipFontSize,
      showSerieLabels
    } = this.props;
    let chart = prevChart? prevChart: null;

    if(!chart){
      chart = am4core.create(chartId, am4charts.XYChart);
      const am4fontColor = am4core.color(fontColor);
      const am4lineColor = am4core.color(lineColor);
      
      // create X axis for range lines
      const valueAxisX = chart.xAxes.push(new am4charts.ValueAxis());
      valueAxisX.renderer.labels.template.disabled = true;
      valueAxisX.renderer.minGridDistance = 400;
      this.setAxisRange(valueAxisX, 0, 2);

      // create Y axis
      this.valueAxisY = chart.yAxes.push(new am4charts.ValueAxis());
      this.valueAxisY.min = 0;
      this.valueAxisY.title.fontSize = titleFontSize;
      this.valueAxisY.title.fontWeight = 500;
      this.valueAxisY.title.fill = am4fontColor;
      this.setFont(this.valueAxisY, axisFontSize, am4fontColor, 400, fontFamily);

      const lowLine = chart.series.push(new am4charts.LineSeries());
      this.setLineField(lowLine, 'x', 'low');
      this.setStroke(lowLine, 1, am4lineColor, dashStyle);
      lowLine.xAxis = valueAxisX;
      lowLine.stacked = true;

      const highLine = chart.series.push(new am4charts.LineSeries());
      this.setLineField(highLine, 'x', 'high');
      this.setStroke(highLine, 1, am4lineColor, dashStyle)
      this.setFillStyle(highLine, am4core.color(rangeFillColor));
      highLine.xAxis = valueAxisX;
      highLine.stacked = true;

      // create category axis for columns
      const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'category';
      categoryAxis.renderer.grid.template.disabled = true;
      this.setFont(categoryAxis, axisFontSize, am4fontColor, 400, fontFamily);

      // create columns
      const columnSeries = chart.series.push(new am4charts.ColumnSeries());
      this.setCategoryField(columnSeries, 'category', 'y');
      this.setStroke(columnSeries, 0);
      columnSeries.xAxis = categoryAxis;
      columnSeries.columns.template.width = am4core.percent(40);

      // set column colors
      chart.colors.list = [
        am4core.color('#00BCD4'),
        am4core.color('#E0E0E0')
      ];
      this.setColumnColor(columnSeries, chart);

      if (showSerieLabels) {
        // create labelBullet text for columns
        const labelBullet = columnSeries.bullets.push(new am4charts.LabelBullet());
        labelBullet.label.text = '{valueY}';
        labelBullet.dy = -10;  // set vertical position of the label
        labelBullet.label.fill = am4fontColor;
      } else {
        // create tooltips
        columnSeries.columns.template.strokeOpacity = 0;
        columnSeries.columns.template.tooltipText = "{valueY}";
        columnSeries.tooltip.getFillFromObject = false;
        columnSeries.tooltip.label.fontSize = tooltipFontSize;
        columnSeries.tooltip.background.fill = am4core.color('#FFFFFF');
        columnSeries.tooltip.label.fill = am4core.color(tooltipFontColor);
        columnSeries.tooltip.tooltipContainer.exportable = false;
      }
    }

    if (this.valueAxisY.title.text !== yAxisTitle) {
      this.valueAxisY.title.text = yAxisTitle;
    }

    if ( !isEqual(data, this.prevChartData) ) {
      const chartData = cloneDeep(data);
      chartData[0].low = chartData[1].low = chartData[0].y > chartData[1].y? chartData[1].y : chartData[0].y;
      chartData[0].high = chartData[1].high = Math.abs(chartData[0].y - chartData[1].y);
      chart.data = chartData;
      this.prevChartData = chartData;
    }

    return chart;
  }

  setAxisRange(axis, min, max){
    axis.min = min;
    axis.max = max;  
  }

  setFont(axis, size, color, weight = 400, fontFamily){
    axis.fontSize = size;
    axis.fontWeight = weight;
    axis.renderer.labels.template.fill = color;
    axis.fontFamily = fontFamily;
  }

  setStroke(series, width, color, dash ) {
    series.strokeWidth = width;
    series.stroke = color;
    series.strokeDasharray = dash;
  }
  
  setFillStyle(series, color, opacity = 1) {
    series.fill = color;
    series.fillOpacity = opacity;
  }
  
  setLineField(series, valueX, valueY) {
    series.dataFields.valueX = valueX;
    series.dataFields.valueY = valueY;
  }

  setCategoryField(series, valueX, valueY) {
    series.dataFields.categoryX = valueX;
    series.dataFields.valueY = valueY;
  }

  setColumnColor(series, chart){
    series.columns.template.adapter.add('fill', (value, target) => {
      return chart.colors.getIndex(target.dataItem.index);
    });
  }
}
